import type { FC } from 'react';
import { useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography } from '@mui/material';

import type { DashboardWidgetProps } from 'src/types/dashboard';
import { getCurrentLocale } from 'src/helper/helper';
import { apiPaths } from 'src/services/api/apiPaths';
import { useFilterContext } from 'src/components/app/context/FilterContext';
import WidgetFrame from 'src/components/dashboard/DashboardPage/components/WidgetFrame';
import type { StackedNumbersList } from 'src/components/followUp/common/types';
import WidgetHeaderWithPill from 'src/components/followUp/common/WidgetHeaderWithPill/WidgetHeaderWithPill';
import useGenerateWidgetEndpoint from 'src/components/followUp/hooks/useGenerateWidgetEndpoint';
import useWidgetQuery from 'src/components/followUp/hooks/useWidgetQuery';

import type { OrgUnitResponseRate } from './helpers';
import {
  convertApiOrgUnitToOrgUnit,
  getResponseRateForOrgUnit,
} from './helpers';
import { useExportOptions } from './hooks';

import { styles } from './index.styles';

const DataRow: FC<{
  orgUnit: OrgUnitResponseRate;
  stylesKey: keyof typeof styles;
  locale: 'de-DE' | 'en-US';
  noLabel?: boolean;
}> = ({ orgUnit, stylesKey, locale, noLabel }) => (
  <Box sx={styles[stylesKey]}>
    <Typography component="span">{noLabel ? '' : orgUnit.label}</Typography>
    <Typography component="span">{orgUnit.invitees}</Typography>
    <Typography component="span">{orgUnit.respondents}</Typography>
    <Typography component="span">
      {getResponseRateForOrgUnit(orgUnit, locale)}%
    </Typography>
  </Box>
);

const DetailedOrgUnitResponseRateWidget: FC<DashboardWidgetProps> = ({
  config,
}) => {
  const { t } = useTranslation(['followUp']);
  const locale = getCurrentLocale();
  const ariaId = useId();

  const endpoint = useGenerateWidgetEndpoint(
    apiPaths.WIDGET_DATA.DETAILED_ORG_UNIT_RESPONSE_RATE,
    config.id,
  );

  const { data, comparisonData, isFetching, errorMsg, refetch, queryFilters } =
    useWidgetQuery<StackedNumbersList>(endpoint);
  const { filterGroups } = useFilterContext();

  // If there is only one filtered org unit, we get the data for it and its children
  // If there are multiple, we get only data for the selected, and there is no parent
  const { parentOrgUnit, orgUnits, comparisonUnits, comparisonParentOrgUnit } =
    !data?.results.length
      ? {
          parentOrgUnit: null,
          orgUnits: null,
          comparisonUnits: null,
          comparisonParentOrgUnit: null,
        }
      : filterGroups.orgUnit.length > 1
        ? {
            parentOrgUnit: null,
            comparisonParentOrgUnit: null,
            orgUnits: data?.results.map(convertApiOrgUnitToOrgUnit) ?? null,
            comparisonUnits:
              comparisonData?.results.map(convertApiOrgUnitToOrgUnit) ?? null,
          }
        : {
            parentOrgUnit: convertApiOrgUnitToOrgUnit(data.results[0]),
            comparisonParentOrgUnit: comparisonData
              ? convertApiOrgUnitToOrgUnit(comparisonData?.results[0])
              : null,
            orgUnits: data.results.slice(1).map(convertApiOrgUnitToOrgUnit),
            comparisonUnits:
              comparisonData?.results
                .slice(1)
                .map(convertApiOrgUnitToOrgUnit) ?? null,
          };

  const headers = useMemo(
    () => [
      t('statistics.orgUnit'),
      t('statistics.invitees'),
      t('statistics.respondents'),
      t('statistics.responseRate'),
    ],
    [t, locale],
  );

  const exportOptions = useExportOptions({
    parentOrgUnit,
    orgUnits,
    widgetName: config.name,
    queryFilters,
    parentOrgUnitComparison: comparisonParentOrgUnit,
    orgUnitsComparison: comparisonUnits,
  });

  return (
    <WidgetFrame
      ariaId={ariaId}
      refetch={refetch}
      loading={isFetching}
      error={errorMsg}
      minHeight="auto"
      exportOptions={exportOptions}
      header={<WidgetHeaderWithPill title={config.name} ariaId={ariaId} />}
    >
      <Box sx={styles.tableScrollContainer}>
        <Box sx={styles.tableContainer}>
          <Box sx={styles.tableHeader}>
            {headers.map((text) => (
              <Typography
                variant="body3"
                key={text}
                sx={{
                  fontWeight: 600,
                }}
              >
                {text}
              </Typography>
            ))}
          </Box>
          {parentOrgUnit && (
            <Stack
              direction="column"
              sx={{
                gap: 1,
                height: 132,
              }}
            >
              <DataRow
                orgUnit={parentOrgUnit}
                stylesKey="primaryTableRow"
                locale={locale}
              />
              {comparisonParentOrgUnit && (
                <DataRow
                  orgUnit={comparisonParentOrgUnit}
                  stylesKey="comparisonRow"
                  locale={locale}
                  noLabel
                />
              )}
            </Stack>
          )}
          {orgUnits?.map((childUnit) => {
            const comparisonUnit = comparisonUnits?.find(
              (u) => u.label === childUnit.label,
            );
            return (
              <Stack
                key={childUnit.label}
                direction="column"
                sx={{
                  gap: 1,
                  height: 132,
                }}
              >
                <DataRow
                  orgUnit={childUnit}
                  stylesKey="secondaryTableRow"
                  locale={locale}
                />
                {comparisonUnit && (
                  <DataRow
                    orgUnit={comparisonUnit}
                    stylesKey="comparisonRow"
                    locale={locale}
                    noLabel
                  />
                )}
              </Stack>
            );
          })}
        </Box>
      </Box>
    </WidgetFrame>
  );
};

export default DetailedOrgUnitResponseRateWidget;
